import { useMemo } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import {
    selectAssignee,
    makeSelectRequiresWhatsAppTemplateReply,
} from 'flow/selectors/TicketsSelectors';
import {
    selectContactName,
    selectOwnExtensionKey,
} from 'flow/selectors/ExtensionSelectors';
import { getStringWithFirstLetterFromSecondWord } from 'flow/utils';
import {
    CHANNEL,
    ChannelValues,
} from 'flow/components/tickets/messageChannels';
import { TicketKey } from 'flow/types/EntityKeys';

interface UseExternalMessagePlaceholderParams {
    ticketKey: TicketKey;
    externalChannelSelected?: ChannelValues;
    allowInputInExternalReply: boolean;
    unlockButtonClicked: boolean;
}

export const useExternalMessagePlaceholder = ({
    ticketKey,
    externalChannelSelected,
    allowInputInExternalReply,
    unlockButtonClicked,
}: UseExternalMessagePlaceholderParams): string => {
    const { i18next } = useAppContext();

    const assignee = useAppSelector((state) =>
        selectAssignee(state, ticketKey),
    );

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const assigneeName = useAppSelector((state) =>
        assignee === 'unassigned'
            ? i18next.t('tickets.unassigned')
            : selectContactName(state, assignee),
    );

    const selectRequiresWhatsAppTemplateReply = useMemo(
        makeSelectRequiresWhatsAppTemplateReply,
        [],
    );
    const whatsAppTemplateReplyRequired = useAppSelector((state) =>
        selectRequiresWhatsAppTemplateReply(state, ticketKey),
    );

    const showWhatsAppInactivityTemplateState =
        whatsAppTemplateReplyRequired &&
        (assignee === ownExtensionKey || unlockButtonClicked);

    return useMemo(() => {
        if (!allowInputInExternalReply && assignee === 'unassigned')
            return i18next.t('tickets.input.assign_and_reply');
        if (showWhatsAppInactivityTemplateState)
            return i18next.t('tickets.locked_inactivity');
        if (!allowInputInExternalReply && assignee !== ownExtensionKey)
            return i18next.t('tickets.input.assigned_reply_anyway', {
                assignee: getStringWithFirstLetterFromSecondWord(assigneeName),
            });
        switch (externalChannelSelected) {
            case CHANNEL.SMS:
                return i18next.t('tickets.input.sms.helptext');
            case CHANNEL.WHATSAPP:
                return i18next.t('tickets.whatsapp.reply_send');
            case CHANNEL.EMAIL:
                return i18next.t('tickets.email.reply_send');
            default:
                return i18next.t('message.input.reply'); //TO DO: replace with i18key
        }
    }, [
        externalChannelSelected,
        allowInputInExternalReply,
        assignee,
        ownExtensionKey,
        assigneeName,
        showWhatsAppInactivityTemplateState,
        i18next,
    ]);
};
